import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { CheckCircle } from 'lucide-react';

const forgotPasswordSchema = z.object({
  email: z.string().email('Invalid email address'),
});

type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>;

const ForgotPasswordPage = () => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
  });

  const onSubmit = async (data: ForgotPasswordFormData) => {
    setIsLoading(true);
    setError('');

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3000'}/api/password-reset/request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: data.email.toLowerCase().trim()
        }),
      });

      const result = await response.json();

      if (result.success) {
        setIsSubmitted(true);
      } else if (response.status === 429) {
        setError('Too many password reset requests. Please try again later.');
      } else {
        setError(result.error || 'Failed to send reset link');
      }
    } catch (error) {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      <div className="fixed inset-0 bg-gradient-to-br from-white via-[#F0F9FF] to-[#E8F4FD]">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#18A5F9]/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-[#18A5F9]/3 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 min-h-screen flex items-center justify-center">
        <div className="w-full max-w-md p-8">
          <div className="text-center mb-10">
            <img
              src="/assets/logo.svg"
              alt="Research Clever"
              className="h-14 w-auto mx-auto mb-6"
            />
            <p className="text-base text-[#69727D]">
              Reset your password
            </p>
          </div>

          {!isSubmitted ? (
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <div className="md-outlined-textfield">
                  <input
                    type="email"
                    id="email"
                    {...register('email')}
                    className="w-full"
                    placeholder="Enter your email address"
                    disabled={isLoading}
                  />
                </div>
                {errors.email && (
                  <p className="text-red-600 text-sm mt-1">{errors.email.message}</p>
                )}
              </div>

              {error && (
                <div className="text-red-700 text-sm text-center bg-red-50 p-3 border border-red-200 rounded-[10px]">{error}</div>
              )}

              <button
                type="submit"
                disabled={isLoading}
                className="md-btn md-btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Sending reset link...
                  </div>
                ) : (
                  'Send Reset Link'
                )}
              </button>
            </form>
          ) : (
            <div className="text-center">
              <div className="mb-4">
                <CheckCircle className="mx-auto h-12 w-12 text-green-500" />
              </div>
              <h2 className="text-xl font-semibold mb-2">Check Your Email</h2>
              <p className="text-[#69727D] mb-6">
                If a user with this email exists, a password reset link has been sent to your inbox.
              </p>
              <button
                onClick={() => navigate('/admin/login')}
                className="md-btn md-btn-outlined w-full"
              >
                Return to Login
              </button>
            </div>
          )}

          <div className="text-center mt-6">
            <Link
              to="/admin/login"
              className="text-[#18A5F9] hover:text-[#0D8FDE] text-sm font-medium transition-colors"
            >
              Back to Login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
